<template>
  <div class="v2-card v2-contacts" data-qid="project-contacts">
    <!-- Contact persons of a project, in the V2 card style (US 9478) -->
    <div class="v2-card__head">
      <h3>{{ $t('maintenance.third_parties.contacts.title') }}</h3>
      <span v-if="!showOnTask && (getTask('update_contacts') || getGuide('update_contacts'))" class="v2-contacts__help">
        <span class="cursor-pointer task-label-open"
              v-if="getTask('update_contacts')"
        >
          <UpdateSingleTaskModal
              :task="getTask('update_contacts')"
              @reloadContent="reloadContent"
              :editIcon="true"
          ></UpdateSingleTaskModal>
        </span>
        <GuideButton v-if="getGuide('update_contacts')" :guide="getGuide('update_contacts')"></GuideButton>
      </span>
      <span class="icon-btn"
            data-qid="add-contact-button"
            v-if="canEdit"
            v-b-tooltip.hover
            :title="$t('maintenance.third_parties.contacts.add_modal_title')"
            @click="showCreateContactModal()"
      >
        <font-awesome-icon icon="plus"/>
      </span>
    </div>

    <div v-if="contactLoading" class="v2-card__body">
      <font-awesome-icon icon="spinner" class="fa-spin text-muted"/>
    </div>

    <div v-else-if="!totalRows" class="v2-card__body">
      <span class="text-muted" data-qid="contacts-empty">{{ $t('system.no_results') }}</span>
    </div>

    <div v-else class="v2-contacts__list">
      <div v-for="(object, index) in contactTableItems"
           :key="'contact-card-' + index"
           :id="'contacts-row-' + index"
           class="v2-contact"
           @click="openViewContactModal(object)"
           data-qid="contact-row"
      >
        <span class="v2-contact__avatar">{{ initials(object.name) }}</span>
        <div class="v2-contact__main">
          <div class="v2-contact__name text-truncate">{{ object.name }}</div>
          <div class="v2-contact__meta">
            <span v-if="object.email" class="text-truncate"><font-awesome-icon icon="envelope" class="mr-1"/>{{ object.email }}</span>
            <span v-if="object.phone"><font-awesome-icon icon="phone" class="mr-1"/>{{ object.phone }}</span>
          </div>
        </div>
        <div class="v2-contact__actions" v-if="project.status !== 'closed' && project.status !== 'completed'">
          <span class="icon-btn"
                v-b-tooltip.hover
                :title="$t('system.edit')"
                @click.stop="showEditContactsModal(object)"
                data-qid="edit-contact-button"
          >
            <font-awesome-icon icon="pen"/>
          </span>
          <span class="icon-btn btn-delete"
                v-b-tooltip.hover
                :title="$t('system.delete')"
                @click.stop="showContactConfirmDeleteModal(object)"
                data-qid="delete-contact-button"
          >
            <font-awesome-icon icon="trash-alt"/>
          </span>
        </div>
      </div>

      <!-- Pages only when there are more contacts than fit on one -->
      <div class="v2-contacts__pager" v-if="rows > selectedPerPage">
        <b-pagination v-model="currentPage"
                      :total-rows="rows"
                      :per-page="selectedPerPage"
                      size="sm"
                      @change="pageChanged"
                      class="mb-0"
        ></b-pagination>
      </div>
    </div>

    <!-- Delete contact -->
    <EngagementConfirmModal ref="delete-contact"
                            :title="$t('maintenance.measures.delete.title')"
                            :eyebrow="$t('maintenance.third_parties.contacts.title')"
                            :confirm-label="$t('maintenance.measures.delete.submit')"
                            :busy="buttonDisabled"
                            qid="delete-contact"
                            @confirm="confirmDeleteContact(itemToDelete)"
    >
      <p v-if="itemToDelete && itemToDelete.id" class="mb-0">
        {{ $t('maintenance.measures.delete.description') }} <b>{{ itemToDelete.name }}</b>. {{ $t('maintenance.measures.delete.are_you_sure') }}
      </p>
    </EngagementConfirmModal>

    <!-- Add contact -->
    <b-modal ref="add-contact"
             modal-class="task-modal v2-modal"
             @hide="hideCreateContactModal"
    >
      <template #modal-header>
        <div class="v2-modal__head">
          <div class="v2-modal__titles">
            <div class="v2-modal__eyebrow">{{ $t('maintenance.third_parties.contacts.title') }}</div>
            <h3 qid="add-contact-title">{{ $t('maintenance.third_parties.contacts.add_modal_title') }}</h3>
          </div>
          <span class="task-panel__nav" @click="hideCreateContactModal" data-qid="add-contact-close"><font-awesome-icon icon="times"/></span>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="add-contact-error">{{ error }}</b-alert>

      <b-form id="add-contact-form" @submit="formSubmit" qid="add-contact-form">
        <b-form-group>
          <label class="us-field-label" for="add-contact-email">{{ $t('maintenance.third_parties.contacts.create.email_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="add-contact-email"
                        qid="add-contact-form-email"
                        type="email"
                        v-model="form.email"
                        required
                        @change="loadUserByEmail"
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.contacts.create.email_placeholder')"
          ></b-form-input>
          <small class="form-text text-muted">{{ $t('maintenance.third_parties.contacts.create.user_tip') }}</small>
        </b-form-group>
        <b-form-group>
          <label class="us-field-label" for="add-contact-name">{{ $t('maintenance.third_parties.create.name_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="add-contact-name"
                        qid="add-contact-form-name"
                        type="text"
                        v-model="form.name"
                        required
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.create.name_placeholder')"
          ></b-form-input>
        </b-form-group>
        <b-form-group class="mb-0">
          <label class="us-field-label" for="add-contact-phone">{{ $t('maintenance.third_parties.contacts.create.phone_label') }}</label>
          <b-form-input id="add-contact-phone"
                        qid="add-contact-form-phone"
                        type="text"
                        v-model="form.phone"
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.contacts.create.phone_placeholder')"
          ></b-form-input>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <div class="v2-modal__footer">
          <b-button variant="secondary" @click="hideCreateContactModal" qid="add-contact-cancel-button">
            {{ $t('activities.measures.create.cancel') }}
          </b-button>
          <b-button type="submit" form="add-contact-form" variant="success" :disabled="buttonDisabled" qid="add-contact-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" size="sm" class="fa-spin mr-1"/>{{ $t('activities.measures.create.submit') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Edit contact -->
    <b-modal ref="edit-contact"
             modal-class="task-modal v2-modal"
             @hide="hideEditContactModal"
    >
      <template #modal-header>
        <div class="v2-modal__head">
          <div class="v2-modal__titles">
            <div class="v2-modal__eyebrow">{{ $t('maintenance.third_parties.contacts.title') }}</div>
            <h3 qid="edit-contact-title">{{ $t('maintenance.third_parties.contacts.edit_modal_title') }}</h3>
          </div>
          <span class="task-panel__nav" @click="hideEditContactModal" data-qid="edit-contact-close"><font-awesome-icon icon="times"/></span>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="edit-contact-error">{{ error }}</b-alert>

      <b-form id="edit-contact-form" @submit="formEditSubmit" qid="edit-contact-form">
        <b-form-group>
          <label class="us-field-label" for="edit-contact-email">{{ $t('maintenance.third_parties.contacts.create.email_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="edit-contact-email"
                        qid="edit-contact-form-email"
                        type="email"
                        v-model="editForm.email"
                        required
                        @change="loadEditUserByEmail"
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.contacts.create.email_placeholder')"
          ></b-form-input>
          <small class="form-text text-muted">{{ $t('maintenance.third_parties.contacts.create.user_tip') }}</small>
        </b-form-group>
        <b-form-group>
          <label class="us-field-label" for="edit-contact-name">{{ $t('maintenance.third_parties.create.name_label') }}<span class="mandatory"> *</span></label>
          <b-form-input id="edit-contact-name"
                        qid="edit-contact-form-name"
                        type="text"
                        v-model="editForm.name"
                        required
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.create.name_placeholder')"
          ></b-form-input>
        </b-form-group>
        <b-form-group class="mb-0">
          <label class="us-field-label" for="edit-contact-phone">{{ $t('maintenance.third_parties.contacts.create.phone_label') }}</label>
          <b-form-input id="edit-contact-phone"
                        qid="edit-contact-form-phone"
                        type="text"
                        v-model="editForm.phone"
                        :maxLength="255"
                        :placeholder="$t('maintenance.third_parties.contacts.create.phone_placeholder')"
          ></b-form-input>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <div class="v2-modal__footer">
          <b-button variant="secondary" @click="hideEditContactModal" qid="edit-contact-cancel-button">
            {{ $t('activities.measures.create.cancel') }}
          </b-button>
          <b-button type="submit" form="edit-contact-form" variant="success" :disabled="buttonDisabled" qid="edit-contact-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" size="sm" class="fa-spin mr-1"/>{{ $t('system.save') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- View contact -->
    <b-modal ref="view-contact"
             modal-class="task-modal v2-modal"
             @hide="hideViewContactModal"
    >
      <template #modal-header>
        <div class="v2-modal__head">
          <div class="v2-modal__titles">
            <div class="v2-modal__eyebrow">{{ $t('maintenance.third_parties.contacts.view_modal_title') }}</div>
            <h3 qid="view-contact-name">{{ selectedViewItem ? selectedViewItem.name : '' }}</h3>
          </div>
          <span class="task-panel__nav" @click="hideViewContactModal" data-qid="view-contact-close"><font-awesome-icon icon="times"/></span>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="view-contact-error">{{ error }}</b-alert>

      <div v-if="selectedViewItem" class="engagement-facts mb-0">
        <div class="engagement-facts__item">
          <span class="engagement-facts__label">{{ $t('maintenance.third_parties.contacts.create.email_label') }}</span>
          <a v-if="selectedViewItem.email" :href="'mailto:' + selectedViewItem.email" qid="view-contact-email">{{ selectedViewItem.email }}</a>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>
        <div class="engagement-facts__item">
          <span class="engagement-facts__label">{{ $t('maintenance.third_parties.contacts.create.phone_label') }}</span>
          <span v-if="selectedViewItem.phone" qid="view-contact-phone">{{ selectedViewItem.phone }}</span>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>
      </div>

      <template #modal-footer>
        <div class="v2-modal__footer">
          <b-button v-if="canEdit && project.status !== 'closed' && project.status !== 'completed'"
                    variant="secondary"
                    @click="editFromView"
                    qid="view-contact-edit-button"
          >
            <font-awesome-icon icon="pen" class="mr-1"/>{{ $t('system.edit') }}
          </b-button>
          <b-button variant="success" @click="hideViewContactModal" qid="view-contact-cancel-button">
            {{ $t('system.close') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import roleCheck from "@/util/ACL/roleCheck"
  import GuideButton from '@/components/guide/GuideButton.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import EngagementConfirmModal from '@/views/Projects/Engagement/EngagementConfirmModal.vue';


  export default {
    name: 'ContactsTable',
    data: function() {
      return {
        form: {
          name: '',
          email: '',
          phone: '',
          category: [],
          type: [],
        },
        editForm: {
          name: '',
          email: '',
          phone: '',
          category: [],
          type: []
        },
        error: '',
        editError: '',
        loadingItems: false,
        selectedTableItem: [],
        contactTableItems: [],
        contactLoading: true,
        itemToDelete: {},
        itemObject: {},
        buttonDisabled: false,
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        selectedItem: {},
        selectedParentDocument: {},
        viewMeasureModal: false,
        myGuides: [],
        selectedAnswer: [],
        selectedDate: '',
        selectedNumber: '',
        inputLinkName: '',
        inputLink: '',
        allAnswers: [],
        selectedFiles: [],
        checklistOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0},
        ],
        showHistoricDocuments: false,
        hasEndDate: 1,
        selectedViewItem: {},
        foundUser: {},
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      tasks: {
        type: Array
      }
    },
    components: {
      Avatar,
      GuideButton,
      UpdateSingleTaskModal,
      EngagementConfirmModal
    },
    computed: {
      // Contacts are added on open projects, and not from the client a project is shared with
      canEdit() {
        return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.project) &&
          this.project.status != 'closed' && this.project.status != 'completed' &&
          ((this.project.additional_client && this.getClient.slug != this.project.additional_client.slug) || this.project.additional_client == null);
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getDeletedUserImage() {
        let link = require('@/assets/deleted-user-avatar.png');
        let url = 'url(' + link + ')';
        return url
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContacts();
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.project.id && this.$store.state.updatedObject.who_am_i == this.project.who_am_i) {
              this.project = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContacts()
      }, 400),
      checkIfAdmin() {
        if (roleCheck('Client admin') || roleCheck('Partition admin')) {
          return true
        } else {
          return false
        }
      },
      reloadContent() {
        this.loadContacts();
      },
      loadUserByEmail() {
        let self = this
        piincHttp.get('users', { params:
          {
            email: this.form.email,
            client: this.getClient.id
          }
        }).then(function(response) {
          self.loading = false;
          if (response.data.items && response.data.items.length) {
            self.foundUser = response.data.items[0]
            self.form.name = self.foundUser.name;
            self.form.phone = self.foundUser.phone;
          }
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      loadEditUserByEmail() {
        let self = this
        this.foundUser = {}
        piincHttp.get('users', { params:
          {
            email: this.editForm.email,
            client: this.getClient.id
          }
        }).then(function(response) {
          self.loading = false;
          if (response.data.items && response.data.items.length) {
            self.foundUser = response.data.items[0]
            self.editForm.name = self.foundUser.name;
            self.editForm.phone = self.foundUser.phone;
          }
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      initials(name) {
        return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('');
      },
      editFromView() {
        const contact = this.selectedViewItem;
        this.hideViewContactModal();
        this.showEditContactsModal(contact);
      },
      openViewContactModal(object) {
        this.selectedViewItem = object
        this.$refs['view-contact'].show()
      },
      hideViewContactModal () {
        this.selectedViewItem = []
        this.$refs['view-contact'].hide()
      },
      handleCategoryInput(input) {
        this.categoryInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContacts();
      },
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Third party' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Third party',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.form.links.splice(index, 1);
      },
      showCreateContactModal() {
        this.form.name = ''
        this.form.email = ''
        this.form.phone = ''
        this.form.category = []
        this.form.type = []
        this.categoryInput = ''
        this.foundUser = {}

        this.$refs['add-contact'].show()
      },
      hideCreateContactModal() {
        this.error = ''
        this.selectedTableItem = []
        this.loadContacts()
        // this.loadCategories();
        this.$refs['add-contact'].hide()
      },
      showEditContactsModal(object) {
        this.selectedTableItem = object
        this.editForm.name = ''
        this.editForm.category = []
        this.categoryInput = ''
        this.editError = ''
        this.foundUser = {}

        if (object.category) {
          this.editForm.category = []
          this.editForm.category = _.find(this.categoryItems, function(item) {
            return item.category === object.category;
          });
        }

        this.editForm.name = object.name;
        this.editForm.email = object.email;
        this.editForm.phone = object.phone;

        this.$refs['edit-contact'].show()
      },
      hideEditContactModal() {
        this.editError = ''
        this.editForm.name = '';
        this.editForm.email = '';
        this.editForm.phone = '';
        this.loadContacts()
        this.$refs['edit-contact'].hide()
      },
      showContactConfirmDeleteModal(object) {
        this.itemToDelete = object;
        this.$refs['delete-contact'].show();
      },
      hideContactConfirmDeleteModal() {
        this.itemToDelete = {};
        this.loadContacts();
        this.$refs['delete-contact'].hide();
      },
      confirmDeleteContact(object) {
        let self = this;
        piincHttp.delete('projects/' + this.project.slug + '/contacts/' + object.id).then(function() {
          self.hideContactConfirmDeleteModal();
          self.loadContacts();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.third_parties.contacts.delete_success'))
        }, function() {});
      },
      formSubmit(evt) {
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        formData.append("name", this.form.name);
        formData.append("email", this.form.email);

        if (this.form.phone) {
          formData.append("phone", this.form.phone);
        }

          this.buttonDisabled = true;
          piincHttp.post('/projects/' + this.project.slug + '/contacts', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function() {
            self.loadContacts(self.currentPage);
            self.$toastr('success', self.$t('maintenance.third_parties.contacts.create_success'))
            self.hideCreateContactModal();

            self.$emit('updated');
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
      },
      formEditSubmit(evt) {

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

          piincHttp.put('/projects/' + this.project.slug + '/contacts/' + this.selectedTableItem.id, this.editForm).then(function() {
            self.hideEditContactModal();
            self.loadContacts(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.third_parties.contacts.update_success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.editError = error.data.message;
            }
          })
      },
      loadContacts(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        piincHttp.get('projects/' + self.project.slug + '/contacts', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: self.selectedSort !== '' ? self.selectedSort : undefined,
            order: self.selectedDirection !== '' ? self.selectedDirection : undefined,
            keyword: self.keywordValue !== '' ? self.keywordValue : undefined,
            client: self.getClient.slug
          }
        }).then(function(response) {
          self.contactLoading = false;
          self.contactTableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContacts(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        // Load Third parties when page changed listener is triggered
        this.loadContacts(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContacts(1, self.selectedPerPage)

      },
    },
    created () {
      this.project = this.object

      if (this.project && this.project.id) {
        // Table items
        this.loadContacts();
      }

      this.loadGuides('update_contacts')
    }
  }
</script>
